import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  EnhancedECMRListItem,
  getEnhancedECMRList, 
  deleteECMRFromSource,
  isDocumentListAccessPersistenceUnavailableError,
  isDocumentListAccessStateChangedError,
} from '../services/eCMRService';
import AuthStatusBanner from './AuthStatusBanner';
import { safeConsoleError } from '../utils/safeConsole';

const ECMRList: React.FC = () => {
  const [ecmrList, setEcmrList] = useState<EnhancedECMRListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [listAccessIssue, setListAccessIssue] = useState<
    'state_changed' | 'persistence_unavailable' | 'unavailable' | null
  >(null);
  const [exampleMessage, setExampleMessage] = useState<{ show: boolean, type: 'success' | 'error' | 'info', message: string }>({
    show: false,
    type: 'success',
    message: ''
  });

  const showMessage = useCallback((type: 'success' | 'error' | 'info', message: string) => {
    setExampleMessage({ show: true, type, message });
    setTimeout(() => {
      setExampleMessage(prev => ({ ...prev, show: false }));
    }, 5000);
  }, []);

  const loadECMRList = useCallback(async () => {
    setLoading(true);
    setListAccessIssue(null);
    try {
      const list = await getEnhancedECMRList();
      // Cloud-only list: stale local rows are non-authoritative and must not be offered in production UI.
      const validList = list.filter((item): item is EnhancedECMRListItem => Boolean(
        item && item.id && item.title && item.source === 'api'
      ));
      setEcmrList(validList);
    } catch (error) {
      safeConsoleError('Error loading ECMR list', 'document.list.load');
      setListAccessIssue(
        isDocumentListAccessStateChangedError(error)
          ? 'state_changed'
          : isDocumentListAccessPersistenceUnavailableError(error)
            ? 'persistence_unavailable'
            : 'unavailable'
      );
      setEcmrList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadECMRList();
  }, [loadECMRList]);

  const handleDeleteDocument = async (documentId: string, source: EnhancedECMRListItem['source'], title: string) => {
    if (!window.confirm(`¿Estás seguro de que quieres eliminar "${title}"?`)) {
      return;
    }

    try {
      const success = await deleteECMRFromSource(documentId, source);
      if (success) {
        showMessage('success', `Documento "${title}" eliminado correctamente`);
        loadECMRList();
      } else {
        showMessage('error', 'Error al eliminar el documento');
      }
    } catch (error) {
      showMessage('error', 'No fue posible eliminar el documento.');
    }
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return 'Fecha desconocida';
    
    // Si ya es una fecha formateada (contiene "/")
    if (dateStr.includes('/')) {
      return dateStr;
    }
    
    // Format: YYYYMMDDHHMMSS
    if (dateStr.length === 14) {
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      const hour = dateStr.substring(8, 10);
      const minute = dateStr.substring(10, 12);
      
      return `${day}/${month}/${year} ${hour}:${minute}`;
    }
    
    return dateStr;
  };

  const getMessageStyle = (type: 'success' | 'error' | 'info') => {
    switch (type) {
      case 'success':
        return 'bg-green-100 border-l-4 border-green-500 text-green-700';
      case 'error':
        return 'bg-red-100 border-l-4 border-red-500 text-red-700';
      case 'info':
        return 'bg-blue-100 border-l-4 border-blue-500 text-blue-700';
      default:
        return 'bg-gray-100 border-l-4 border-gray-500 text-gray-700';
    }
  };

  const getParticipantRoleLabel = (role: string): string => {
    switch (role) {
      case 'consignor':
        return 'Consignador';
      case 'carrier':
        return 'Transportista';
      case 'consignee':
        return 'Consignatario';
      case 'authority':
        return 'Autoridad';
      case 'delegate':
        return 'Delegado';
      default:
        return role;
    }
  };

  const getParticipantDisplayName = (participant: NonNullable<EnhancedECMRListItem['participants']>[number]): string =>
    participant.name || participant.email || participant.userId || 'Sin identificar';

  const getStatusLabel = (status: EnhancedECMRListItem['status']): string => {
    switch (status) {
      case 'draft':
        return 'Borrador';
      case 'issued':
        return 'Emitido';
      case 'accepted_by_carrier':
        return 'Aceptado por transportista';
      case 'in_transit':
        return 'En tránsito';
      case 'delivered':
        return 'Entregado';
      case 'closed':
        return 'Cerrado';
      case 'amended':
        return 'Modificado';
      case 'cancelled':
        return 'Cancelado';
      default:
        return 'Borrador';
    }
  };

  const canEditDocument = (status: EnhancedECMRListItem['status']): boolean =>
    status === 'draft' || status === 'amended';

  const canDeleteDocument = (status: EnhancedECMRListItem['status']): boolean =>
    status === 'draft' || status === 'cancelled';

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">Documentos e-CMR</h1>
      
      <AuthStatusBanner />
      
      {exampleMessage.show && (
        <div className={`mb-4 p-4 rounded ${getMessageStyle(exampleMessage.type)}`}>
          <p>{exampleMessage.message}</p>
        </div>
      )}
      
      <div className="mb-6 flex flex-col sm:flex-row justify-between gap-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <Link to="/create" className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded w-full sm:w-auto text-center">
            <i className="fas fa-plus mr-2"></i> Crear Nuevo
          </Link>
          <Link to="/import" className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded w-full sm:w-auto text-center">
            <i className="fas fa-file-import mr-2"></i> Importar XML a nube
          </Link>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-2">
          <button 
            onClick={loadECMRList} 
            className="bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded w-full sm:w-auto disabled:opacity-50"
            disabled={loading}
          >
            <i className="fas fa-sync-alt mr-2"></i> Actualizar
          </button>
        </div>
      </div>
      
      {loading ? (
        <div className="bg-white shadow-md rounded p-6 text-center">
          <p className="text-lg">Cargando documentos...</p>
        </div>
      ) : listAccessIssue ? (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4" role="alert">
          <h3 className="font-bold">Lista de documentos no disponible</h3>
          <p>
            {listAccessIssue === 'state_changed'
              ? 'Un documento cambio durante la consulta. Recarga la lista vigente antes de continuar.'
              : listAccessIssue === 'persistence_unavailable'
                ? 'No se pudo registrar la evidencia de acceso al listado. La consulta permanece bloqueada temporalmente.'
                : 'No se pudo cargar la lista auditada desde el backend.'}
          </p>
          <button
            type="button"
            onClick={loadECMRList}
            className="mt-4 bg-red-700 hover:bg-red-800 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
            disabled={loading}
          >
            <i className="fas fa-sync-alt mr-2"></i> Reintentar
          </button>
        </div>
      ) : ecmrList.length === 0 ? (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4">
          <h3 className="font-bold">No hay documentos e-CMR en la nube</h3>
          <p>Para comenzar, inicia sesión y crea un nuevo e-CMR cloud o importa un XML para guardarlo mediante el backend autenticado.</p>
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <Link to="/create" className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-center">
              <i className="fas fa-plus mr-2"></i> Crear mi primer e-CMR cloud
            </Link>
            <Link to="/import" className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded text-center">
              <i className="fas fa-file-import mr-2"></i> Importar XML a nube
            </Link>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Título
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Participantes
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Origen
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {ecmrList.map((ecmr) => (
                  <tr key={`${ecmr.source}-${ecmr.id}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {ecmr.document_id?.substring(0, 8) || (ecmr.id || '').substring(0, 8)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {ecmr.title}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(ecmr.date)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {ecmr.participants && ecmr.participants.length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {ecmr.participants.map((participant, index) => (
                            <span key={`${participant.role}-${participant.userId || participant.email || participant.name || index}`}>
                              {getParticipantRoleLabel(participant.role)}: {getParticipantDisplayName(participant)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span>Sin participantes</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {getStatusLabel(ecmr.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        ☁️ Nube
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex flex-wrap gap-2">
                        <Link 
                          to={`/view/${ecmr.document_id || ecmr.id}?source=api`}
                          className="text-blue-600 hover:text-blue-900 bg-blue-100 hover:bg-blue-200 px-2 py-1 rounded"
                          title="Ver documento"
                        >
                          <i className="fas fa-eye mr-1"></i> Ver
                        </Link>
                        <>
                          <Link
                            to={`/workflow/${ecmr.document_id || ecmr.id}`}
                              className="text-indigo-600 hover:text-indigo-900 bg-indigo-100 hover:bg-indigo-200 px-2 py-1 rounded"
                              title="Procedimientos y firmas server-side"
                            >
                              <i className="fas fa-shield-alt mr-1"></i> Procedimientos y firmas
                            </Link>
                            <Link
                              to={`/validate/${ecmr.document_id || ecmr.id}?source=api`}
                              className="text-teal-700 hover:text-teal-900 bg-teal-100 hover:bg-teal-200 px-2 py-1 rounded"
                              title="Validación XML document-bound auditable"
                            >
                              <i className="fas fa-check-circle mr-1"></i> Validar
                            </Link>
                            <Link
                              to={`/evidence/${ecmr.document_id || ecmr.id}`}
                              className="text-purple-600 hover:text-purple-900 bg-purple-100 hover:bg-purple-200 px-2 py-1 rounded"
                              title="Registrar evidencias operativas"
                            >
                              <i className="fas fa-clipboard-check mr-1"></i> Evidencias
                            </Link>
                            <Link
                              to={`/retention/${ecmr.document_id || ecmr.id}`}
                              className="text-amber-700 hover:text-amber-900 bg-amber-100 hover:bg-amber-200 px-2 py-1 rounded"
                              title="Retención y legal hold server-side"
                            >
                              <i className="fas fa-archive mr-1"></i> Retención
                            </Link>
                            <Link
                              to={`/amendments/${ecmr.document_id || ecmr.id}`}
                              className="text-pink-700 hover:text-pink-900 bg-pink-100 hover:bg-pink-200 px-2 py-1 rounded"
                              title="Solicitud, acuse y aplicación de modificaciones"
                            >
                              <i className="fas fa-code-branch mr-1"></i> Modificaciones
                            </Link>
                            <Link
                              to={`/audit/${ecmr.document_id || ecmr.id}`}
                              className="text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded"
                              title="Trazabilidad de auditoría backend sanitizada"
                            >
                              <i className="fas fa-list-alt mr-1"></i> Auditoría
                            </Link>
                            <Link
                              to={`/attachments/${ecmr.document_id || ecmr.id}`}
                              className="text-orange-700 hover:text-orange-900 bg-orange-100 hover:bg-orange-200 px-2 py-1 rounded"
                              title="Documentos suplementarios con controles backend"
                            >
                              <i className="fas fa-paperclip mr-1"></i> Adjuntos
                            </Link>
                            <Link
                              to={`/evidence-dashboard/${ecmr.document_id || ecmr.id}`}
                              className="text-emerald-700 hover:text-emerald-900 bg-emerald-100 hover:bg-emerald-200 px-2 py-1 rounded"
                              title="Panel role-aware de evidencias backend"
                            >
                              <i className="fas fa-chart-line mr-1"></i> Panel evidencias
                            </Link>
                            <Link
                              to={`/final-xml/${ecmr.document_id || ecmr.id}`}
                              className="text-cyan-700 hover:text-cyan-900 bg-cyan-100 hover:bg-cyan-200 px-2 py-1 rounded"
                              title="Recuperar XML final archivado con verificación backend"
                            >
                              <i className="fas fa-file-code mr-1"></i> XML final
                            </Link>
                          </>
                        {canEditDocument(ecmr.status) ? (
                          <Link 
                            to={`/edit/${ecmr.document_id || ecmr.id}?source=api`}
                            className="text-green-600 hover:text-green-900 bg-green-100 hover:bg-green-200 px-2 py-1 rounded"
                            title="Editar documento"
                          >
                            <i className="fas fa-edit mr-1"></i> Editar
                          </Link>
                        ) : (
                          <button
                            type="button"
                            disabled
                            className="text-gray-500 bg-gray-100 px-2 py-1 rounded cursor-not-allowed"
                            title="El contenido es inmutable en este estado"
                          >
                            <i className="fas fa-edit mr-1"></i> Editar
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteDocument(
                            ecmr.document_id || ecmr.id,
                            'api',
                            ecmr.title
                          )}
                          disabled={!canDeleteDocument(ecmr.status)}
                          className={`px-2 py-1 rounded ${
                            canDeleteDocument(ecmr.status)
                              ? 'text-red-600 hover:text-red-900 bg-red-100 hover:bg-red-200'
                              : 'text-gray-500 bg-gray-100 cursor-not-allowed'
                          }`}
                          title={canDeleteDocument(ecmr.status) ? 'Eliminar documento' : 'La eliminación no está permitida en este estado'}
                        >
                          <i className="fas fa-trash mr-1"></i> Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      <div className="mt-8 bg-blue-50 border-l-4 border-blue-500 text-blue-700 p-4 rounded">
        <h3 className="font-bold text-lg mb-2">¿Qué es un e-CMR?</h3>
        <p className="mb-2">
          El e-CMR (carta de porte electrónica) es la versión digital del documento CMR utilizado en el transporte internacional de mercancías por carretera.
        </p>
        <p>
          Esta aplicación usa un baseline técnico UNECE eCMR/CMR con runtime XML/XSD controlado por el backend y D24A solo como interoperabilidad explícita; no constituye una certificación legal completa.
        </p>
      </div>
    </div>
  );
};

export default ECMRList;
